import { orderBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { selectWidths } from '~/common/constants/selectWidths';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Select } from '~/eds/src/components/Select/Select';
import { useDebouncedCallback } from '~/hooks/useDebouncedCallback';
import { api } from '~/redux/api/createApi';
import { Company } from '~/redux/api/methods/workflows/types';
import { Nullable } from '~/types/Nullable';

import {
  CompanyOption,
  CompanyValue as CompanyValueType,
} from '../types/fields';
import { WorkflowQuestionFormField } from './types';
import { useIntegrationErrorToast } from './useIntegrationErrorToast';

export const CompanyWorkflowQuestion = ({
  question,
  onChange,
  ...rest
}: WorkflowQuestionFormField<CompanyValueType>) => {
  const intl = useIntl();
  const { isPanelEnlarged } = useAppConfigContext();

  const {
    field: { value },
  } = question;

  const [selectedCompany, setSelectedCompany] =
    useState<Nullable<CompanyOption>>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [
    getCompanies,
    { data: companies = [], isFetching, error: fetchError, isSuccess },
  ] = api.endpoints.getIntegrateWorkflowCompanies.useLazyQuery();

  const debouncedGetCompanies = useDebouncedCallback(getCompanies, null);

  const options: CompanyOption[] = useMemo(() => {
    if (searchQuery.length < 2) {
      return [];
    }
    return companies.map((company: Company) => ({
      label: company.descriptor,
      value: company.id,
    }));
  }, [companies, searchQuery]);

  const { connectionErrorMessage } = useIntegrationErrorToast({
    isError: Boolean(fetchError),
    isSuccess,
  });

  useEffect(() => {
    if (value?.value?.resource_id && value?.value?.default_value) {
      setSelectedCompany({
        label: value.value.default_value,
        value: value.value.resource_id,
      });
    } else {
      setSelectedCompany(null);
    }
  }, [value?.value?.resource_id, value?.value?.default_value]);

  const optionsWithSelected = useMemo(() => {
    const allOptions = selectedCompany
      ? [selectedCompany, ...options]
      : options;
    return orderBy(uniqBy(allOptions, 'value'), ['label'], ['asc']);
  }, [options, selectedCompany]);

  const handleChange = useCallback(
    (selectedValue: string | string[] | null) => {
      const valueString = Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue;

      const selectedOption = valueString
        ? optionsWithSelected.find((opt) => opt.value === valueString)
        : null;

      setSelectedCompany(selectedOption || null);

      if (selectedOption) {
        onChange(
          {
            default_value: selectedOption.label,
            resource_id: selectedOption.value,
          },
          'value',
        );
      } else {
        onChange(null, 'value');
      }
    },
    [onChange, optionsWithSelected],
  );

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setSearchQuery(searchQuery);
      if (searchQuery.length >= 2) {
        debouncedGetCompanies({
          searchString: searchQuery,
        });
      } else {
        debouncedGetCompanies.cancel?.();
      }
    },
    [debouncedGetCompanies],
  );

  return (
    <Box w={isPanelEnlarged ? selectWidths.l : selectWidths.fullWidth}>
      <FormField
        {...rest}
        input={Select}
        placeholder={intl.formatMessage({
          id: 'CompanyWorkflowQuestion.placeholder',
          defaultMessage: 'Search and select a company...',
          description: 'Placeholder text for company selection in intake form',
        })}
        value={selectedCompany?.value ?? null}
        onChange={handleChange}
        error={connectionErrorMessage ? undefined : rest.error}
        footer={connectionErrorMessage}
        inputProps={{
          options: optionsWithSelected,
          isLoading: isFetching,
          onSearch: handleSearch,
          enablePortal: true,
          isMulti: false,
          width: '100%',
          noOptionsMessage:
            connectionErrorMessage ||
            intl.formatMessage({
              id: 'CompanyWorkflowQuestion.noOptionsMessage',
              defaultMessage: 'No companies found',
              description:
                'Message shown when no companies match the search query',
            }),
        }}
      />
    </Box>
  );
};
